import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { ISO8601DateTime } from "../types/common";
import type {
  IPlanOption,
  OrganizationUsageType,
  PaymentPlanType,
} from "../types/organization";
import { toFirstUpperCaseOtherLowerCase } from "./string-util";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Seoul");

const getPlanSubName = (plan: PaymentPlanType) => {
  switch (plan) {
    case "EARLY-BUTTER":
      return "얼리버터";
    case "FREE-TRIAL":
      return "체험";
    default:
      return null;
  }
};

const getPlanName = (plan: PaymentPlanType) => {
  switch (plan) {
    case "EARLY-BUTTER":
      return "Free";
    case "FREE-TRIAL":
      return "Free";
    default:
      return toFirstUpperCaseOtherLowerCase(plan);
  }
};

const getDefaultPlan = (_now: dayjs.Dayjs = dayjs.tz()) => {
  return "FREE-TRIAL";
};

const getDefaultPlanStartAt = (now: dayjs.Dayjs = dayjs.tz()) => {
  return now.format("YYYY-MM-DDT00:00:00+09:00");
};

const getDefaultPlanEndAt = (now: dayjs.Dayjs = dayjs.tz()) => {
  return now.add(1, "month").add(-1, "day").format("YYYY-MM-DDT23:59:59+09:00");
};

const getNextPlanStartAt = (planStartAt: dayjs.Dayjs) => {
  return planStartAt.tz().add(1, "month").format("YYYY-MM-DDT00:00:00+09:00");
};

const getNextPlanEndAt = (planEndAt: dayjs.Dayjs) => {
  return planEndAt.tz().add(1, "month").format("YYYY-MM-DDT23:59:59+09:00");
};

const getMaxMau = (plan: PaymentPlanType, planOption: IPlanOption) => {
  if (planOption.maxMau) {
    return planOption.maxMau;
  }

  if (plan === "STARTER") {
    return 10000;
  } else if (plan === "BASIC") {
    return 40000;
  } else if (plan === "PRO") {
    return 100000;
  } else if (plan === "BUSINESS") {
    return -1;
  } else if (plan === "FREE-TRIAL") {
    return -1;
  } else if (plan === "EARLY-BUTTER") {
    return -1;
  }

  return 2000;
};

const getMaxSite = (plan: PaymentPlanType, planOption: IPlanOption) => {
  if (planOption.maxSite) {
    return planOption.maxSite;
  }

  if (plan === "STARTER") {
    return 1;
  } else if (plan === "BASIC") {
    return 3;
  } else if (plan === "PRO") {
    return 5;
  } else if (plan === "BUSINESS") {
    return -1;
  } else if (plan === "EARLY-BUTTER") {
    return -1;
  }

  return 1;
};

const getMaxUser = (plan: PaymentPlanType, planOption: IPlanOption) => {
  if (planOption.maxUser) {
    return planOption.maxUser;
  }

  if (plan === "STARTER") {
    return 3;
  } else if (plan === "BASIC") {
    return 5;
  } else if (plan === "PRO") {
    return -1;
  } else if (plan === "BUSINESS") {
    return -1;
  } else if (plan === "EARLY-BUTTER") {
    return -1;
  }

  return 1;
};

const getMaxCampaign = (plan: PaymentPlanType, planOption: IPlanOption) => {
  if (planOption.maxCampaign) {
    return planOption.maxCampaign;
  }

  if (plan === "STARTER") {
    return 10;
  } else if (plan === "BASIC") {
    return 30;
  } else if (plan === "PRO") {
    return -1;
  } else if (plan === "BUSINESS") {
    return -1;
  } else if (plan === "EARLY-BUTTER") {
    return -1;
  }

  return 1;
};

export enum PLAN_AVAILABLE_TYPE {
  OK = 0,
  OVER_MAU = 1,
  OVER_CAMPAIGN = 2,
  OVER_SITE = 3,
  OVER_USER = 4,
}

const checkPlanAvailable = (
  plan: PaymentPlanType,
  planOption: IPlanOption,
  mau: number,
  siteCount: number = 0,
  userCount: number = 0,
  campaignCount: {
    popupCount: number;
    groupPopupCount: number;
    bannerCount: number;
    instagramCount: number;
    totalCount: number;
  } = {
    popupCount: 0,
    groupPopupCount: 0,
    bannerCount: 0,
    instagramCount: 0,
    totalCount: 0,
  }
) => {
  // 1. check mau
  if (mau > getMaxMau(plan, planOption)) {
    if (getMaxMau(plan, planOption) > 0) {
      return PLAN_AVAILABLE_TYPE.OVER_MAU;
    }
  }

  // 2. check site
  if (siteCount > getMaxSite(plan, planOption)) {
    if (getMaxSite(plan, planOption) > 0) {
      return PLAN_AVAILABLE_TYPE.OVER_SITE;
    }
  }

  // 3. check 사용자
  if (userCount > getMaxUser(plan, planOption)) {
    if (getMaxUser(plan, planOption) > 0) {
      return PLAN_AVAILABLE_TYPE.OVER_USER;
    }
  }

  // 4. check 캠페인
  if (plan === "FREE" || plan === "FREE-TRIAL") {
    if (
      campaignCount.popupCount > 1 ||
      campaignCount.groupPopupCount > 1 ||
      campaignCount.bannerCount > 1 ||
      campaignCount.instagramCount > 1
    ) {
      return PLAN_AVAILABLE_TYPE.OVER_CAMPAIGN;
    }
  } else {
    const maxCampaign = getMaxCampaign(plan, planOption);
    if (maxCampaign !== -1 && campaignCount.totalCount > maxCampaign) {
      return PLAN_AVAILABLE_TYPE.OVER_CAMPAIGN;
    }
  }

  return PLAN_AVAILABLE_TYPE.OK;
};

const checkCreateAvailable = (
  plan: PaymentPlanType,
  planOption: IPlanOption,
  campaignCount: number,
  totalCampaignCount: number
) => {
  if (plan === "FREE" || plan === "FREE-TRIAL") {
    if (campaignCount > 1) {
      return false;
    }
  } else {
    const maxCampaign = getMaxCampaign(plan, planOption);
    if (maxCampaign !== -1 && totalCampaignCount > maxCampaign) {
      return false;
    }
  }

  return true;
};

/**
 * 다음 예상 플랜
 * 1. 현재 플랜보다 같거나 높아질 수만 있음
 * 2. MAU는 배치에서만 10% 초과 인정할 수 있도록 (mau / 1.1) 값으로 넘김
 * 3. 사이트수, 유저수, 캠페인수 조건 체크
 */
const getNextRenewalPlan = (
  currentPlan: PaymentPlanType,
  mau: number,
  siteCount: number = 0,
  userCount: number = 0,
  campaignCount: {
    popupCount: number;
    groupPopupCount: number;
    bannerCount: number;
    instagramCount: number;
    totalCount: number;
  } = {
    popupCount: 0,
    groupPopupCount: 0,
    bannerCount: 0,
    instagramCount: 0,
    totalCount: 0,
  }
): PaymentPlanType => {
  if (currentPlan === "BUSINESS") {
    return "BUSINESS";
  }

  const planList: PaymentPlanType[] = ["FREE", "STARTER", "BASIC", "PRO"];
  const startIdx =
    planList.indexOf(currentPlan) === -1 ? 0 : planList.indexOf(currentPlan);
  for (let i = startIdx; i < planList.length; i++) {
    const checkPlan = planList[i] as PaymentPlanType;
    const check = checkPlanAvailable(
      checkPlan,
      {},
      mau,
      siteCount,
      userCount,
      campaignCount
    );
    if (check === PLAN_AVAILABLE_TYPE.OK) {
      return checkPlan;
    }
  }

  return "BUSINESS";
};

const getChangePlanUsage = (
  plan: PaymentPlanType,
  key: OrganizationUsageType
) => {
  if (plan === "STARTER") {
    if (key === "MAU") return "10,000 미만 기준";
    else if (key === "CAMPAIGN") return 10;
    else if (key === "SITE") return 1;
    else if (key === "USER") return 3;
  } else if (plan === "BASIC") {
    if (key === "MAU") return "40,000 미만 기준";
    else if (key === "CAMPAIGN") return 30;
    else if (key === "SITE") return 3;
    else if (key === "USER") return 5;
  } else if (plan === "PRO") {
    if (key === "MAU") return "100,000 미만 기준";
    else if (key === "CAMPAIGN") return "무제한";
    else if (key === "SITE") return 5;
    else if (key === "USER") return "무제한";
  }
};

export enum PAYMENT_ALERT_STATUS_TYPE {
  OK = 0,
  NOTICE_EXPECT_PLAN = 1,
  PAYMENT_NOT_PROCEED = 2,
  FAIL_PAYMENT = 3,
  COMEBACK = 4,
}

const getPaymentAlertStatus = (
  _planEndAt: ISO8601DateTime,
  nextBilledAt: ISO8601DateTime,
  billingFailCount: number = 0,
  status: number
) => {
  // const today = dayjs().startOf("day");
  // 서비스 제한
  if (status !== 1) {
    return PAYMENT_ALERT_STATUS_TYPE.FAIL_PAYMENT;
  }

  // 결제 실패 안내 (nextBilledAt이 있고 billingFailCount > 0일 경우)
  if (nextBilledAt && billingFailCount > 0) {
    return PAYMENT_ALERT_STATUS_TYPE.PAYMENT_NOT_PROCEED;
  }

  // 결제 예정일 안내 (종료일 기준 4일 노출)
  // if (
  //   nextBilledAt &&
  //   today.diff(dayjs(planEndAt).startOf("day"), "day") >= 0 &&
  //   today.diff(dayjs(planEndAt).startOf("day"), "day") <= 3
  // ) {
  //   return PAYMENT_ALERT_STATUS_TYPE.NOTICE_EXPECT_PLAN;
  // }

  return PAYMENT_ALERT_STATUS_TYPE.OK;
};

export default {
  getPlanSubName,
  getPlanName,
  getDefaultPlan,
  getDefaultPlanStartAt,
  getDefaultPlanEndAt,
  getMaxMau,
  getMaxSite,
  getMaxUser,
  getMaxCampaign,
  checkPlanAvailable,
  checkCreateAvailable,
  getChangePlanUsage,
  getPaymentAlertStatus,
  getNextPlanStartAt,
  getNextPlanEndAt,
  getNextRenewalPlan,
};
